/** @jsx jsx */
import { jsx } from '@emotion/react';

import type * as React from 'react';
import { useMemo } from 'react';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import { type Skill } from 'bundles/browse-common/types/Skill';
import {
  DEFAULT_SORTED_VALUE,
  NUMBER_OF_RESULTS_FOR_DOMAIN_MOBILE_SEARCH,
  NUMBER_OF_RESULTS_PER_PAGE,
  SEARCH_MAIN_INDEX_ID,
} from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS, IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import type { SearchConfig } from 'bundles/search-common/providers/searchTypes';

type Props = {
  skill: Skill;
  children: React.ReactNode;
};

export const getMobileSearchConfigs = (showCourseraPlusFilter: boolean, skill: Skill): SearchConfig[] => {
  const filterAttributes = [
    ...DEFAULT_SEARCH_FILTERS.filter((filter) => filter !== 'skills'),
    ...(showCourseraPlusFilter ? [IS_PART_OF_COURSERA_PLUS] : []),
  ];

  return [
    {
      id: SEARCH_MAIN_INDEX_ID,
      entityType: 'PRODUCTS',
      limit: NUMBER_OF_RESULTS_FOR_DOMAIN_MOBILE_SEARCH,
      query: skill.skillName,
      facets: filterAttributes,
      facetFilters: [[`skills:${skill.skillName}`]],
      syncQueryWithUrl: true,
      syncFiltersWithUrl: true,
      sortBy: DEFAULT_SORTED_VALUE,
    },
  ];
};

export const getDesktopSearchConfigs = (showCourseraPlusFilter: boolean, skill: Skill): SearchConfig[] => {
  const filterAttributes = [
    ...DEFAULT_SEARCH_FILTERS.filter((filter) => filter !== 'skills'),
    ...(showCourseraPlusFilter ? [IS_PART_OF_COURSERA_PLUS] : []),
  ];

  return [
    {
      id: SEARCH_MAIN_INDEX_ID,
      entityType: 'PRODUCTS',
      limit: NUMBER_OF_RESULTS_PER_PAGE,
      query: skill.skillName,
      facets: filterAttributes,
      facetFilters: [[`skills:${skill.skillName}`]],
      syncQueryWithUrl: true,
      syncFiltersWithUrl: true,
      sortBy: DEFAULT_SORTED_VALUE,
    },
  ];
};

const SkillSearchProvider = (props: Props) => {
  const { skill, children } = props;
  const isMobile = useMediaQuery(breakpoints.down('xs'), { noSsr: true });

  const searchConfigs = useMemo(
    () => (isMobile ? getMobileSearchConfigs(false, skill) : getDesktopSearchConfigs(false, skill)),
    [skill, isMobile]
  );
  return (
    <SearchProvider key={isMobile ? 'mobile' : 'desktop'} searchConfigs={searchConfigs}>
      {children}
    </SearchProvider>
  );
};

export default SkillSearchProvider;
